/**
 * ArbitrationChat Component
 *
 * Real-time chat interface for dispute arbitration
 * Handles message display, sending, and real-time updates
 *
 * Single Responsibility: Only handles chat UI and interactions
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Chip,
  Tooltip,
  Fade,
  Button,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import GavelIcon from '@mui/icons-material/Gavel';
import LockIcon from '@mui/icons-material/Lock';
import { IChatMessage, IParticipant } from '../../services/arbitrationService';
import imageUploadService from '../../services/imageUpload.service';

interface ArbitrationChatProps {
  messages: IChatMessage[];
  participants: IParticipant[];
  loading?: boolean;
  sending?: boolean;
  typingUsers?: Map<string, string>;
  onSendMessage: (content: string, imageUrl?: string) => Promise<void>;
  onTyping?: () => void;
  onStopTyping?: () => void;
  currentUserId?: string;
  disabled?: boolean;
  /** صفحه‌بندی پیام‌های قدیمی‌تر (چت‌های بیش از ۵۰ پیام) */
  hasMoreMessages?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  /** وضعیت آنلاین شرکت‌کنندگان — برای هدر چت */
  onlineUsers?: Set<string>;
  onViewProfile?: (userId: string) => void;
}

// Role to icon mapping
const RoleIcons: Record<string, React.ReactNode> = {
  buyer: <PersonIcon fontSize="small" />,
  seller: <StoreIcon fontSize="small" />,
  admin: <AdminPanelSettingsIcon fontSize="small" />,
  party_a: <SwapHorizIcon fontSize="small" />,
  party_b: <SwapHorizIcon fontSize="small" />
};

// Role to color mapping (hex — alpha tints work in dark + light)
const RoleColors: Record<string, string> = {
  buyer: '#0ea5e9',
  seller: '#f59e0b',
  admin: '#8b5cf6',
  party_a: '#06b6d4',
  party_b: '#10b981'
};

// Role labels in Persian
const RoleLabels: Record<string, string> = {
  buyer: 'خریدار',
  seller: 'فروشنده',
  admin: 'داور',
  user: 'کاربر',
  unknown: 'کاربر',
  party_a: 'طرف اول',
  party_b: 'طرف دوم'
};

const dayKeyOf = (timestamp: string): string => {
  const d = new Date(timestamp);
  return isNaN(d.getTime()) ? '' : d.toDateString();
};

const dayLabelOf = (timestamp: string): string => {
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  if (d.toDateString() === today.toDateString()) return 'امروز';
  if (d.toDateString() === yesterday.toDateString()) return 'دیروز';
  return d.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * Single Message Bubble Component
 * React.memo: جلوگیری از re-render همه پیام‌ها با هر تغییر state (مثل تایپینگ)
 */
const MessageBubble = React.memo<{
  message: IChatMessage;
  isOwn: boolean;
  showAvatar: boolean;
  isDark: boolean;
}>(({ message, isOwn, showAvatar, isDark }) => {
  const roleColor = RoleColors[message.senderRole] || '#64748b';

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
  };

  // کارت ویژه «نتیجه داوری» — آخرین پیام چت‌های بسته‌شده
  if (message.messageType === 'system' && message.systemData?.action === 'dispute_verdict') {
    const meta = message.systemData?.metadata || {};
    const resolvedAt = meta.resolvedAt ? new Date(meta.resolvedAt) : new Date(message.timestamp);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2.5, px: 1 }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 460,
            borderRadius: '16px',
            overflow: 'hidden',
            border: `1.5px solid ${alpha('#8b5cf6', 0.6)}`,
            boxShadow: `0 4px 16px ${alpha('#8b5cf6', 0.18)}`
          }}
        >
          <Box sx={{ bgcolor: '#7c3aed', color: '#fff', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon sx={{ fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>نتیجه داوری</Typography>
            <Typography variant="caption" sx={{ ml: 'auto', opacity: 0.85, direction: 'ltr' }}>
              {resolvedAt.toLocaleString('fa-IR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1.5 }}>
            {meta.decision && (
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: alpha('#8b5cf6', isDark ? 0.2 : 0.1),
                  color: isDark ? '#c4b5fd' : '#6D28D9',
                  borderRadius: '8px',
                  px: 1.25,
                  py: 0.5,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  mb: 1
                }}
              >
                {meta.decision}
              </Box>
            )}
            {meta.summary && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: meta.notes ? 0.75 : 0 }}>
                {meta.summary}
              </Typography>
            )}
            {meta.notes && meta.notes !== meta.summary && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  px: 1.25,
                  py: 0.75
                }}
              >
                {meta.notes}
              </Typography>
            )}
            {!meta.decision && !meta.summary && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message.content}</Typography>
            )}
          </Box>
          <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', px: 2, py: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              🔒 این گفتگو با ثبت رأی{meta.resolvedByName ? ` توسط ${meta.resolvedByName}` : ''} بسته شد
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  // System messages
  if (message.messageType === 'system') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
        <Chip
          label={message.content}
          size="small"
          variant="outlined"
          sx={{
            bgcolor: 'action.hover',
            borderColor: 'divider',
            color: 'text.secondary',
            fontSize: '0.72rem',
            height: 'auto',
            py: 0.25,
            '& .MuiChip-label': { whiteSpace: 'normal' }
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isOwn ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 1,
        mb: showAvatar ? 1.25 : 0.5
      }}
    >
      {/* Avatar */}
      {showAvatar ? (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: alpha(roleColor, isDark ? 0.35 : 0.16),
            color: roleColor,
            border: `2px solid ${alpha(roleColor, 0.55)}`,
            fontSize: '0.85rem'
          }}
        >
          {RoleIcons[message.senderRole] || message.senderName?.charAt(0) || '?'}
        </Avatar>
      ) : (
        <Box sx={{ width: 32, flexShrink: 0 }} />
      )}

      {/* Message Content */}
      <Box sx={{ maxWidth: '72%', minWidth: 110 }}>
        {/* Sender Name */}
        {showAvatar && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              mb: 0.4,
              flexDirection: isOwn ? 'row-reverse' : 'row'
            }}
          >
            <Typography variant="caption" sx={{ color: roleColor, fontWeight: 800 }}>
              {message.senderName}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10, color: alpha(roleColor, 0.8) }}>
              {RoleLabels[message.senderRole] || message.senderRole}
            </Typography>
          </Box>
        )}

        {/* Bubble */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2.5,
            borderStartStartRadius: isOwn ? 20 : 6,
            borderStartEndRadius: isOwn ? 6 : 20,
            ...(isOwn
              ? {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  boxShadow: (t) => `0 2px 10px ${alpha(t.palette.primary.main, 0.25)}`
                }
              : {
                  bgcolor: alpha(roleColor, isDark ? 0.14 : 0.07),
                  border: `1px solid ${alpha(roleColor, isDark ? 0.35 : 0.22)}`,
                  color: 'text.primary'
                })
          }}
        >
          {/* Image message */}
          {message.messageType === 'image' && message.file && (
            <Box
              component="a"
              href={message.file.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'block', mb: message.content ? 1 : 0 }}
            >
              <Box
                component="img"
                src={message.file.url}
                alt="تصویر"
                loading="lazy"
                sx={{ maxWidth: '100%', maxHeight: 220, borderRadius: 1.5, display: 'block' }}
              />
            </Box>
          )}

          {/* File message */}
          {message.messageType === 'file' && message.file && (
            <Box
              component="a"
              href={message.file.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                bgcolor: isOwn ? 'rgba(255,255,255,0.18)' : 'background.paper',
                borderRadius: 1.5,
                textDecoration: 'none',
                color: 'inherit',
                mb: message.content ? 1 : 0
              }}
            >
              <AttachFileIcon fontSize="small" />
              <Box>
                <Typography variant="body2">{message.file.name}</Typography>
                <Typography variant="caption" sx={{ color: isOwn ? 'rgba(255,255,255,0.75)' : 'text.secondary' }}>
                  {(message.file.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
            </Box>
          )}

          {/* Text content */}
          {message.content && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7 }}>
              {message.content}
            </Typography>
          )}

          {/* Time */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'left',
              mt: 0.4,
              opacity: 0.65,
              fontSize: '0.62rem',
              direction: 'ltr'
            }}
          >
            {formatTime(message.timestamp)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

/**
 * Typing Indicator Component
 */
const TypingIndicator: React.FC<{ users: Map<string, string> }> = ({ users }) => {
  if (users.size === 0) return null;

  const names = Array.from(users.values());
  const text = names.length === 1
    ? `${names[0]} در حال تایپ...`
    : `${names.slice(0, -1).join('، ')} و ${names[names.length - 1]} در حال تایپ...`;

  return (
    <Fade in>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'text.disabled',
                animation: 'bounce 1.4s infinite ease-in-out',
                animationDelay: `${i * 0.16}s`,
                '@keyframes bounce': {
                  '0%, 80%, 100%': { transform: 'scale(0)' },
                  '40%': { transform: 'scale(1)' }
                }
              }}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {text}
        </Typography>
      </Box>
    </Fade>
  );
};

/**
 * Main ArbitrationChat Component
 */
const ArbitrationChat: React.FC<ArbitrationChatProps> = ({
  messages,
  participants,
  loading = false,
  sending = false,
  typingUsers = new Map(),
  onSendMessage,
  onTyping,
  onStopTyping,
  currentUserId,
  disabled = false,
  hasMoreMessages = false,
  loadingOlder = false,
  onLoadOlder,
  onlineUsers = new Set(),
  onViewProfile
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [inputValue, setInputValue] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // فقط وقتی «آخرین» پیام عوض شود اسکرول کن — نه هنگام prepend پیام‌های قدیمی‌تر
  const lastMessageIdRef = useRef<string | null>(null);
  useEffect(() => {
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    if (lastId && lastId !== lastMessageIdRef.current) {
      scrollToBottom();
    }
    lastMessageIdRef.current = lastId;
  }, [messages, scrollToBottom]);

  // Handle file select
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('لطفاً فقط فایل‌های تصویری انتخاب کنید');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم فایل نباید از 5 مگابایت بیشتر باشد');
      return;
    }

    setSelectedFile(file);

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to storage
    try {
      setUploadingImage(true);
      const result = await imageUploadService.uploadImage(file, 'chat');
      if (result) {
        setUploadedImageUrl(result.url);
      } else {
        alert('خطا در آپلود تصویر به سرور');
        clearSelectedFile();
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('خطا در آپلود تصویر');
      clearSelectedFile();
    } finally {
      setUploadingImage(false);
    }
  };

  // Clear selected file
  const clearSelectedFile = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setUploadedImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle send message
  const handleSend = async () => {
    if ((!inputValue.trim() && !uploadedImageUrl) || sending || disabled || uploadingImage) return;

    const content = inputValue.trim();
    const imageUrl = uploadedImageUrl;
    setInputValue('');
    clearSelectedFile();
    onStopTyping?.();

    try {
      await onSendMessage(content, imageUrl || undefined);
    } catch (error) {
      // Restore input on error
      setInputValue(content);
    }
  };

  // Handle input change with typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onTyping?.();
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Group messages by sender for avatar display
  const shouldShowAvatar = (index: number): boolean => {
    if (index === 0) return true;
    const current = messages[index];
    const previous = messages[index - 1];
    return current.senderId !== previous.senderId;
  };

  /** پیام‌ها + جداکنندهٔ روز */
  const renderMessages = () => {
    const out: React.ReactNode[] = [];
    let lastDay = '';
    messages.forEach((message, index) => {
      const day = dayKeyOf(message.timestamp);
      if (day && day !== lastDay) {
        lastDay = day;
        out.push(
          <Box key={`day-${day}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1.75 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.25,
                borderRadius: 5,
                bgcolor: theme.palette.action.hover,
                color: 'text.secondary',
                fontWeight: 700,
                flexShrink: 0
              }}
            >
              {dayLabelOf(message.timestamp)}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
        );
      }
      out.push(
        <MessageBubble
          key={message.id}
          message={message}
          isOwn={message.senderId === currentUserId}
          showAvatar={shouldShowAvatar(index)}
          isDark={isDark}
        />
      );
    });
    return out;
  };

  // شرکت‌کنندگان در هدر چت — طرفین اول، داور آخر
  const headerParticipants = [...participants].sort((a, b) => {
    const order: Record<string, number> = { buyer: 0, party_a: 0, seller: 1, party_b: 1, admin: 2 };
    return (order[a.role] ?? 9) - (order[b.role] ?? 9);
  });

  const onlineCount = participants.filter((p) => onlineUsers.has(p.userId)).length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        overflow: 'hidden'
      }}
    >
      {/* Chat header — شرکت‌کنندگان */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
          flexShrink: 0,
          bgcolor: 'background.paper'
        }}
      >
        {headerParticipants.map((p) => {
          const roleColor = RoleColors[p.role] || '#64748b';
          const isOnline = onlineUsers.has(p.userId);
          const isTyping = typingUsers.has(p.userId);
          return (
            <Tooltip
              key={p.userId}
              title={
                <Box sx={{ direction: 'rtl' }}>
                  <div>{p.name} · {RoleLabels[p.role] || p.role}</div>
                  {p.email && <div style={{ direction: 'ltr' }}>{p.email}</div>}
                  <div>{isOnline ? 'آنلاین' : 'آفلاین'}</div>
                  {onViewProfile && <div>برای مشاهدهٔ پروفایل کلیک کنید</div>}
                </Box>
              }
            >
              <Box
                onClick={onViewProfile ? () => onViewProfile(p.userId) : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  pl: 1.25,
                  pr: 0.5,
                  py: 0.4,
                  borderRadius: 5,
                  border: `1px solid ${alpha(roleColor, isDark ? 0.35 : 0.25)}`,
                  bgcolor: alpha(roleColor, isDark ? 0.1 : 0.05),
                  cursor: onViewProfile ? 'pointer' : 'default',
                  transition: 'background-color .15s',
                  '&:hover': { bgcolor: alpha(roleColor, isDark ? 0.18 : 0.1) }
                }}
              >
                <Box sx={{ position: 'relative', display: 'flex' }}>
                  <Avatar
                    src={p.avatar}
                    sx={{
                      width: 26,
                      height: 26,
                      bgcolor: alpha(roleColor, isDark ? 0.35 : 0.16),
                      color: roleColor,
                      fontSize: 12,
                      '& svg': { fontSize: 15 }
                    }}
                  >
                    {RoleIcons[p.role] || p.name?.charAt(0) || '?'}
                  </Avatar>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -1,
                      left: -1,
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: isOnline ? 'success.main' : 'text.disabled',
                      border: `2px solid ${theme.palette.background.paper}`
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 9.5,
                      display: 'block',
                      lineHeight: 1.2,
                      color: isTyping ? 'success.main' : roleColor,
                      fontWeight: isTyping ? 700 : 500,
                      ...(isTyping && {
                        animation: 'hdrTyping 1.5s infinite',
                        '@keyframes hdrTyping': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } }
                      })
                    }}
                  >
                    {isTyping ? 'در حال تایپ…' : RoleLabels[p.role] || p.role}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', flexShrink: 0, fontSize: 10.5 }}>
          {onlineCount} آنلاین · {participants.length} نفر
        </Typography>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 1.25, md: 2 },
          py: 1.5,
          bgcolor: isDark ? alpha('#000', 0.25) : alpha('#64748b', 0.05)
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <GavelIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              هنوز پیامی ارسال نشده است
            </Typography>
            <Typography variant="caption" color="text.secondary">
              اولین پیام را ارسال کنید تا گفتگو شروع شود
            </Typography>
          </Box>
        ) : (
          <>
            {hasMoreMessages && onLoadOlder && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onLoadOlder}
                  disabled={loadingOlder}
                  sx={{ borderRadius: 5, px: 2, fontSize: 12 }}
                >
                  {loadingOlder ? <CircularProgress size={14} sx={{ ml: 1 }} /> : null}
                  پیام‌های قبلی
                </Button>
              </Box>
            )}
            {renderMessages()}
            <TypingIndicator users={typingUsers} />
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          flexShrink: 0
        }}
      >
        {disabled ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 0.75 }}>
            <LockIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              این گفتگو بسته شده و امکان ارسال پیام وجود ندارد
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Image Preview */}
            {imagePreview && (
              <Box sx={{ mb: 1, position: 'relative', display: 'inline-block' }}>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="پیش‌نمایش"
                  sx={{
                    maxWidth: 180,
                    maxHeight: 130,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'block'
                  }}
                />
                <IconButton
                  size="small"
                  onClick={clearSelectedFile}
                  disabled={uploadingImage}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                    width: 22,
                    height: 22
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
                {uploadingImage && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 2
                    }}
                  >
                    <CircularProgress size={22} sx={{ color: 'white' }} />
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              {/* Image Button */}
              <Tooltip title="ارسال تصویر">
                <span>
                  <IconButton
                    color="primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage || sending}
                    sx={{
                      bgcolor: (t) => alpha(t.palette.primary.main, isDark ? 0.15 : 0.08),
                      '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, isDark ? 0.25 : 0.15) }
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              {/* Input Field */}
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="پیام خود را بنویسید…"
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                inputRef={inputRef}
                disabled={sending || uploadingImage}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 5,
                    bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#64748b', 0.06)
                  }
                }}
              />

              {/* Send Button */}
              <IconButton
                onClick={handleSend}
                disabled={(!inputValue.trim() && !uploadedImageUrl) || sending || uploadingImage}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': {
                    bgcolor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled
                  }
                }}
              >
                {sending || uploadingImage ? (
                  <CircularProgress size={18} sx={{ color: 'inherit' }} />
                ) : (
                  <SendIcon sx={{ fontSize: 19, transform: 'rotate(180deg)' }} />
                )}
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ArbitrationChat;
